import Link from 'next/link';
import React from 'react';

import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { Button } from '@web/ui/components';
import { ArrowLinkIcon } from '@web/ui/icons';

import { HubTile } from '../HubTile';

import CoinIcon from './coin.svg';
import { fetchPoolData } from './geckoTerminalService';
import { UnaChart } from './UnaChart';
import { env } from '@web/utils/env';

const SHubTile = styled(HubTile)`
  overflow: visible;
`;

const Container = styled.div`
  display: flex;
  align-self: stretch;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('2000')};
  padding-top: ${({ theme }) => theme.spacing('2500')};
  padding-bottom: ${({ theme }) => theme.spacing('2000')};
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing('1000')};
  align-self: stretch;
`;

const Value = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('0500')};
  font: ${({ theme }) => theme.typography.mono.regular};
  color: ${({ theme }) => theme.color.onSilver};
  font-size: ${({ theme }) => theme.typescale.size['10']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
`;

const FdvInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('0500')};
  font: ${({ theme }) => theme.typography.mono.regular};
  color: ${({ theme }) => theme.color.onSilver};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const Badge = styled.div<{ negative?: boolean }>`
  display: flex;
  padding: ${({ theme }) => theme.spacing('0500')};
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
  border: 1px solid ${({ theme }) => theme.color.onSilver};
  background: ${({ theme, negative }) =>
    negative ? theme.color.redFixed : theme.color.greenFixed};
  color: ${({ theme, negative }) =>
    negative ? theme.color.onRedFixed : theme.color.onGreenFixed};
  border: 1px solid
    ${({ theme, negative }) =>
      negative ? theme.color.onRedFixed : theme.color.onSilver};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  text-transform: ${({ theme }) => theme.textTransform.none};
`;

const StyledArrowLinkIcon = styled(ArrowLinkIcon)<{ negative?: boolean }>`
  transition: transform 180ms ease;
  transform: ${({ negative }) => (negative ? 'rotate(180deg)' : 'none')};
  width: ${({ theme }) => theme.size['0625']};
  height: ${({ theme }) => theme.size['0625']};
`;

const formatCurrency = (value: string, isLarge = false): string => {
  const num = parseFloat(value);
  if (isLarge && num >= 1000000) {
    return `$${(num / 1000000).toFixed(2)}M`;
  }
  return `$${num.toFixed(4)}`;
};

const formatPercentage = (value: string): string => {
  const num = parseFloat(value);
  const sign = num >= 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
};

export const UnaTokenChartTile = () => {
  const t = useTranslations();
  const { data, isLoading } = useQuery({
    queryKey: ['una-pool-data'],
    queryFn: fetchPoolData,
    // Same rate-limit story as the chart above.
    refetchInterval: env.SHELL_MODE ? false : 30_000,
    staleTime: env.SHELL_MODE ? 5 * 60_000 : 30_000,
    retry: 3,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
  });

  const tokenPrice = data
    ? formatCurrency(data.base_token_price_usd)
    : '$0.0000';

  const fdvValue = data
    ? formatCurrency(
        (parseFloat(data.base_token_price_usd) * 990627114).toString(),
        true,
      )
    : '$0M';

  const priceChange = data
    ? formatPercentage(data.price_change_percentage.h24)
    : '0.00%';

  const isNegative = data
    ? parseFloat(data.price_change_percentage.h24) < 0
    : false;

  return (
    <SHubTile
      title={t('UNA_TOKEN_CHART_HUB_TILE_TITLE')}
      background="silver"
      textColor="onSilver"
      stateLayer="onSilver"
      badge={
        <Badge negative={isNegative}>
          <StyledArrowLinkIcon negative={isNegative} size="0625" />
          {isLoading ? '...' : priceChange}
        </Badge>
      }
    >
      <Container>
        <DetailsContainer>
          <Value>
            <CoinIcon size="3000" />
            {isLoading ? '$0.0000' : tokenPrice}
          </Value>

          <FdvInfo>
            <span>FDV:</span>
            <span>{isLoading ? '$0M' : fdvValue}</span>
          </FdvInfo>
        </DetailsContainer>
      </Container>

      <UnaChart />

      <Link
        href="https://coinmarketcap.com/currencies/unagi-token/"
        target="_blank"
        rel="noreferrer"
      >
        <Button label={t('UNA_TOKEN_CHART_HUB_TILE_BUTTON')} fullWidth />
      </Link>
    </SHubTile>
  );
};
